import { useCallback, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { setShowRefreshReport } from '../../store/investor/action';
import { Mixpanel } from '../../utils/mixpanel';
import { getSubdomain } from '../../utils/utils';
import { AICA_UNLOCK_REFRESH_OPENED } from '../../utils/constants/mixpanelEvents/investorEvents';
import { VISIBILITY_KEYS } from '../../utils/constants/visibilityKeys';
import { useFeatureVisibility } from '../../utils/useVisibilityConfig';
import { USER_TYPE } from '../../utils/constants/user';
import type { UnlockRefreshOpener } from './types';

interface OrgInfo {
    owners?: {
        owner?: string;
    };
}

export const useUnlockRefreshOpener = (): UnlockRefreshOpener => {
    const dispatch = useAppDispatch();
    const params = useParams();
    const user = useAppSelector((state) => state.user.userData);
    const userType = user.accountType;

    const orgInfo = useAppSelector((state): OrgInfo => state.investor.orgInfo);
    const ownerId = orgInfo?.owners?.owner;
    const ownerDropdownOptions = useAppSelector((state) => state.investor.ownerDropdownOptions);
    const partnerOwner = ownerDropdownOptions.find((item) => item.userId === ownerId);
    const platformUserData = useAppSelector((state) => state.investor.platformUserData);

    const { unlockAicaVisibility } = useFeatureVisibility({
        unlockAicaVisibility: VISIBILITY_KEYS.UNLOCK_AICA.ENABLED,
    });

    const ownerForPersona = userType === USER_TYPE.COMPANY ? platformUserData : partnerOwner;
    const shouldDisableUnlock = unlockAicaVisibility.show && !ownerForPersona;

    const [isTalkToUsOpen, setIsTalkToUsOpen] = useState(false);

    const openUnlockRefresh = useCallback(
        (accessedFrom: string) => {
            if (shouldDisableUnlock) return;
            if (unlockAicaVisibility.show) {
                setIsTalkToUsOpen(true);
            } else {
                dispatch(setShowRefreshReport(true));
            }
            Mixpanel.track(AICA_UNLOCK_REFRESH_OPENED, {
                userRole: userType,
                pnoPlatform: getSubdomain(),
                userId: user?.userId,
                currentUrl: window.location.href,
                organizationId: params.id,
                accessedFrom,
            });
        },
        [shouldDisableUnlock, unlockAicaVisibility.show, dispatch, userType, user?.userId, params.id],
    );

    return {
        shouldDisableUnlock,
        isTalkToUsOpen,
        openTalkToUs: () => setIsTalkToUsOpen(true),
        closeTalkToUs: () => setIsTalkToUsOpen(false),
        openUnlockRefresh,
        contactInput: { userType, platformUserData, partnerOwner },
    };
};
